import type { DeepActivityEvent, DeepSource } from "@/stores/ai-explorer-store"

/**
 * Classify a Deep Explorer tool into the Source Ribbon's bucket icon.
 * Mirrors the inline rules previously embedded in `use-ai-explorer-chat.ts`
 * so the live WS path and the hydrate-on-reload path stay in lockstep.
 */
export const classifyDeepSourceType = (tool: string): DeepSource["type"] => {
  if (tool.includes("library")) return "library"
  if (tool.includes("web")) return "web"
  if (tool.includes("doc") || tool.includes("file")) return "doc"
  if (tool.includes("365") || tool.includes("microsoft")) return "m365"
  return "other"
}

/**
 * Extract DeepSource entries from a single tool_end event's preview_items.
 * Returns `null` (not `[]`) when the event carries no preview payload so the
 * caller can cheaply skip updating state.
 */
export const deriveSourcesFromEvent = (event: DeepActivityEvent): DeepSource[] | null => {
  if (event.event !== "tool_end" || !event.result_summary) return null

  const previewItems = event.result_summary.preview_items as
    | Array<{ title?: string; snippet?: string }>
    | undefined

  if (!previewItems || !Array.isArray(previewItems) || previewItems.length === 0) return null

  const toolType = classifyDeepSourceType(event.tool)

  return previewItems.map((item) => ({
    title: item.title ?? "Untitled",
    snippet: item.snippet?.slice(0, 200),
    type: toolType,
    tool: event.tool,
    timestamp: event.timestamp,
  }))
}

/**
 * Walk a chronological list of DeepActivityEvents and concatenate all
 * DeepSources derived from their preview_items. Used when rehydrating an
 * old chat from persisted `context.deep_activity_events`.
 */
export const deriveSourcesFromActivity = (events: DeepActivityEvent[]): DeepSource[] => {
  const sources: DeepSource[] = []
  for (const event of events) {
    const derived = deriveSourcesFromEvent(event)
    if (derived) sources.push(...derived)
  }
  return sources
}
